import { useState } from 'react';
import { fxApi } from '../../api/services';
import type { FxLineDto, FxRevaluationDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Field, Input, Loadable, Modal, NumberInput, PageHeader, Stat, Table, fmtDate, fmtDateTime, fmtMoney, fmtNumber, money, useAction, useAsync,
} from '../../ui/components';
import { CurrencySelect } from '../../ui/pickers';
import { PageHelp } from '../../ui/help';
import { Link } from '../../router';
import { useCompany } from '../../ui/company';
import { PERM, useCan } from '../../ui/access';

const lastDayOfPreviousMonth = () => {
  const d = new Date();
  d.setDate(0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Diferencial cambiario: revalúa al tipo de cambio de cierre los saldos de las cuentas en moneda extranjera. */
export function FxPage() {
  const list = useAsync(() => fxApi.list(), []);
  const canPost = useCan(PERM.periodClose);
  const [wizard, setWizard] = useState(false);
  const { run, busy } = useAction();
  const cancel = (r: FxRevaluationDto) => run(async (reason) => { await fxApi.cancel(r.id, reason ?? ''); list.reload(); }, {
    confirm: `Se anulará la revaluación de ${r.currencyName} al ${fmtDate(r.date)} con un asiento de reverso. Indique el motivo.`, danger: true, requireReason: true, success: 'Revaluación anulada.',
  });
  return (
    <>
      <PageHeader title="Diferencial cambiario" subtitle="Revaluación de saldos en moneda extranjera"
        actions={canPost ? <Button kind="primary" onClick={() => setWizard(true)}>Nueva revaluación…</Button> : undefined} />
      <PageHelp page="fx" />
      <Loadable state={list}>
        {(rows) => (
          <Table<FxRevaluationDto> rows={rows} rowKey={(r) => r.id} empty="Todavía no hay revaluaciones." columns={[
            { key: 'f', title: 'Fecha', render: (r) => fmtDate(r.date) },
            { key: 'm', title: 'Moneda', render: (r) => r.currencyName },
            { key: 't', title: 'Tipo de cambio', align: 'right', render: (r) => fmtNumber(r.rate, 6) },
            money<FxRevaluationDto>('g', 'Ganancia', (r) => r.gain || null),
            money<FxRevaluationDto>('p', 'Pérdida', (r) => r.loss || null),
            { key: 'a', title: 'Asiento', render: (r) => <Link to={`/entries/${r.entryCode}`}>{r.entryCode}</Link> },
            { key: 's', title: 'Estado', render: (r) => (r.status === 'A' ? <Badge tone="ok">Vigente</Badge> : <Badge tone="bad">Anulada</Badge>) },
            { key: 'c', title: 'Registrada', render: (r) => fmtDateTime(r.createdOn) },
            { key: 'x', title: '', render: (r) => (r.status === 'A' && canPost ? <Button kind="link" busy={busy} onClick={() => cancel(r)}>Anular…</Button> : null) },
          ]} />
        )}
      </Loadable>
      {wizard && <FxWizard onClose={() => setWizard(false)} onDone={() => { setWizard(false); list.reload(); }} />}
    </>
  );
}

function FxWizard({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const company = useCompany();
  const [date, setDate] = useState(lastDayOfPreviousMonth());
  const [currencyId, setCurrencyId] = useState<number | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [asked, setAsked] = useState<{ date: string; currencyId: number; rate: number | null } | null>(null);
  const preview = useAsync(() => fxApi.preview(asked!.date, asked!.currencyId, asked!.rate), [asked?.date, asked?.currencyId, asked?.rate], asked !== null);
  const { run, busy } = useAction();
  return (
    <Modal title="Nueva revaluación" onClose={onClose} size="lg">
      <div className="acc-toolbar">
        <Field label="Fecha de cierre"><Input type="date" value={date} onChange={setDate} /></Field>
        <Field label="Moneda"><CurrencySelect value={currencyId} onChange={setCurrencyId} /></Field>
        <Field label="Tipo de cambio" hint="Vacío = el registrado para la fecha"><NumberInput step="0.000001" min={0} value={rate} onChange={(v) => setRate(v || null)} /></Field>
        <Button disabled={!currencyId} onClick={() => currencyId && setAsked({ date, currencyId, rate })}>Revisar</Button>
      </div>
      {currencyId && currencyId === company?.company.defaultCurrencyId && <Alert kind="warn">Esa es la moneda base de la empresa: elija una moneda extranjera.</Alert>}
      {asked === null && <Alert kind="info">Elija la fecha (normalmente el último día del mes), la moneda y pulse "Revisar". Todavía no se registra nada.</Alert>}
      {asked !== null && (
        <Loadable state={preview}>
          {(p) => (
            <>
              {p.rate > 0 && <p>Tipo de cambio <strong>{fmtNumber(p.rate, 6)}</strong> · {p.rateSource}</p>}
              <div className="acc-stats">
                <Stat label="Ganancia cambiaria" value={fmtMoney(p.gain)} tone="pos" />
                <Stat label="Pérdida cambiaria" value={fmtMoney(p.loss)} tone="neg" />
              </div>
              {p.blockers.map((b) => <Alert key={b} kind="error">{b}</Alert>)}
              {p.blockers.some((b) => b.includes('Parámetros contables')) && <p><Link to="/settings">Ir a Parámetros contables</Link></p>}
              {p.warnings.map((w) => <Alert key={w} kind="warn">{w}</Alert>)}
              {p.lines.length > 0 && (
                <Card title="Cuentas que se ajustan">
                  <Table<FxLineDto> rows={p.lines} rowKey={(l) => `${l.accountNumber}|${l.customerCode ?? ''}`} columns={[
                    { key: 'a', title: 'Cuenta', render: (l) => `${l.accountNumber} · ${l.accountName}` },
                    { key: 'c', title: 'Cliente', render: (l) => l.customerCode ?? '' },
                    money<FxLineDto>('f', `Saldo en ${p.currencyName}`, (l) => l.foreignBalance),
                    money<FxLineDto>('b', 'Saldo en libros', (l) => l.bookBalance),
                    money<FxLineDto>('r', 'Saldo revaluado', (l) => l.revaluedBalance),
                    money<FxLineDto>('d', 'Diferencia', (l) => l.difference),
                  ]} />
                </Card>
              )}
              <div className="acc-actions">
                <Button onClick={onClose}>Cancelar</Button>
                <Button kind="primary" disabled={!p.canPost} busy={busy} onClick={() => run(async () => { await fxApi.post(asked.date, asked.currencyId, asked.rate); onDone(); },
                  { confirm: `Confirma la revaluación de ${p.currencyName} al ${fmtDate(p.date)}?`, success: 'Diferencial cambiario registrado.' })}>Registrar</Button>
              </div>
            </>
          )}
        </Loadable>
      )}
    </Modal>
  );
}
